import type { Theme } from "@outschool/backpack";
import { Box, Tab, Tabs, Typography } from "@outschool/backpack";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { useLocalStorageState } from "@outschool/local-storage";
import { dayjs } from "@outschool/time";
import { ReadOnlyBanner } from "@outschool/ui-components-classroom";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { Container } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useIsSmall, UserAgent } from "@outschool/ui-utils";
import React from "react";

import defaultBanner from "../../../../images/banners/welcome/banner_bg.webp";
import Link from "../../components/Link";
import * as Env from "../../Env";
import { learnerRoutes } from "../../lib/Routes";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import Footer from "../nav/Footer";
import Header from "../nav/Header";
import CommunityBanner, { CommunityBannerProps } from "./CommunityBanner";
import {
  DESKTOP_BANNER_HEIGHT,
  MOBILE_BANNER_HEIGHT_NO_CONTENT,
} from "./consts";
import avatarBuilderImage from "./images/goat-wizard.webp";
import IosAppPromoBanner from "./IosAppPromoBanner";

type HeroLayoutWithTabsProps = {
  currentPath: string;
  children: React.ReactNode;
};

const LOCAL_STORAGE_KEY = "iOS.PromoBanner.FirstShownAt";
const DAYS_TO_SHOW_IOS_BANNER = 7;

function useIosPromoBanner(): boolean | undefined {
  const [iosPromoBannerFirstShownAt, _setIosPromoBannerFirstShownAt] =
    useLocalStorageState(LOCAL_STORAGE_KEY, new Date().toISOString());
  if (
    (UserAgent.isIPhone() || UserAgent.isIPad()) &&
    dayjs().diff(iosPromoBannerFirstShownAt, "day") <= DAYS_TO_SHOW_IOS_BANNER
  ) {
    return true;
  } else {
    return false;
  }
}

function HeroLayoutWithTabs(
  { currentPath, children }: HeroLayoutWithTabsProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const isMobile = useIsMobile();
  const showIosPromoBanner = useIosPromoBanner();
  // community banner content is hard-coded
  // to show, define an object with bannerData fields
  // to remove, set to null
  const liveCommunityBannerData: CommunityBannerProps["bannerData"] | null = {
    title: "Avatar Builder",
    subtitle:
      "Make your own awesome profile picture! Pick your type, art style, and background to create a creature that's totally you.",
    ctaText: "Create Avatar",
    ctaUrl: learnerRoutes.avatarBuilderPath(),
    image: {
      title: "Learner Avatar Builder Image Preview",
      url: avatarBuilderImage,
    },
  };

  const banner: "default" | "ios" | "community" = showIosPromoBanner
    ? "ios"
    : !!liveCommunityBannerData
    ? "community"
    : "default";

  return (
    <Box
      flex
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        backgroundColor: "common.white",
      }}
      ref={ref}
    >
      <Header
        sx={{
          background: isMobile || showIosPromoBanner ? "white" : undefined,
          borderColor: isMobile || showIosPromoBanner ? "#535353" : undefined,
          position: showIosPromoBanner ? "relative" : "absolute",
        }}
        includeBlob={!showIosPromoBanner}
      />
      {banner === "default" && <DefaultBanner />}
      {banner === "ios" && <IosAppPromoBanner />}
      {banner === "community" && liveCommunityBannerData && (
        <CommunityBanner bannerData={liveCommunityBannerData} />
      )}
      {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
      <NavigationTabs currentPath={currentPath} />
      <Container>{children}</Container>
      <Footer />
    </Box>
  );
}

const TABS = [
  {
    label: "Classes",
    path: learnerRoutes.dashboardPath(),
  },
  {
    label: "Search",
    path: learnerRoutes.findClassesPath(),
  },
  {
    label: "Favorites",
    path: learnerRoutes.savedClassesPath(),
  },
];

function NavigationTabs({ currentPath }: { currentPath: string }) {
  const isQuizEnabled = useFeatureFlag("aic-quiz");
  const navTabs = [...TABS];
  if (isQuizEnabled) {
    navTabs.splice(1, 0, {
      label: "Play",
      path: learnerRoutes.playPath(),
    });
  } else {
    navTabs.splice(1, 0, {
      label: "Discovery",
      path: learnerRoutes.discoveryPath(),
    });
  }
  return (
    <Container sx={{ marginBottom: "1em", marginTop: "1em" }}>
      <Tabs variant="scrollable" value={currentPath}>
        {navTabs.map(tab => (
          <Tab
            key={tab.label}
            component={Link}
            label={tab.label}
            value={tab.path}
            to={tab.path}
            trackingName="nav_tab_click"
            sharedProperties={{ tabLabel: tab.label }}
          />
        ))}
      </Tabs>
    </Container>
  );
}

function DefaultBanner() {
  const isSmall = useIsSmall();
  const currentLearner = useCurrentLearner();
  return (
    <Box
      sx={(theme: Theme) => ({
        backgroundImage: `url("${defaultBanner}")`,
        backgroundSize: "cover",
        backgroundPosition: isSmall ? "left top" : "center top",
        height: MOBILE_BANNER_HEIGHT_NO_CONTENT,
        [theme.breakpoints.up("sm")]: {
          height: DESKTOP_BANNER_HEIGHT,
        },
      })}
      data-test-id="default-banner"
    >
      {!isSmall && (
        <Box
          flex
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "48px",
          }}
        >
          <Typography variant="h1" component="div">
            Welcome back,
          </Typography>
          <Typography variant="h1" component="div">
            {currentLearner?.name || "learner"}!
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default React.forwardRef(HeroLayoutWithTabs);
